import { useEffect, useMemo } from "react";
import { App } from "antd";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { OrganizationRoles, Roles } from "@/shared/utils/constants";
import { handleErrors } from "@/shared/utils/forms";
import { usePatchMembershipByIdMutation } from "@/redux/service";
import FormFieldWrapper from "@/components_v2/Wrapper/FormFieldWrapper";
import BaseButton from "@/components_v2/Button/BaseButton";
import BaseModal from "@/components_v2/Modal/BaseModal";
import {
  HookFormBaseDropdown,
  HookFormInput,
} from "@/components_v2/FormFields/ReactHookForm/fields";
import { MembershipFormModalProps } from "@/features/organizations/profile/types";
import { UpdateMembershipRequest } from "@/redux/memberships/types";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";

const MembershipFormModal = ({
  open,
  setOpen,
  defaultData,
}: MembershipFormModalProps) => {
  const { notification } = App.useApp();
  const { t } = useTranslation();

  const currentUser = useSelector(selectCurrentUser);
  const [patchMembershipById] = usePatchMembershipByIdMutation();

  const memberSchema: yup.ObjectSchema<UpdateMembershipRequest> = yup
    .object()
    .shape({
      firstName: yup
        .string()
        .typeError(t("form.labels.notValid"))
        .required(t("form.labels.required")),
      lastName: yup
        .string()
        .typeError(t("form.labels.notValid"))
        .required(t("form.labels.required")),
      role: yup
        .string()
        .typeError(t("form.labels.notValid"))
        .required(t("form.labels.required")),
    });

  const form = useForm({
    resolver: yupResolver(memberSchema),
    mode: "onChange",
    defaultValues: {
      firstName: "",
      lastName: "",
      role: "",
    },
  });

  useEffect(() => {
    if (defaultData) {
      form.reset({
        firstName: defaultData.user.firstName,
        lastName: defaultData.user.lastName,
        role: defaultData.role,
      });
    }
  }, [open, defaultData]);

  const fields = useMemo(() => {
    if (!currentUser) return [];

    const roleOptions =
      currentUser.role === Roles.ADMIN.value
        ? OrganizationRoles.asList()
        : OrganizationRoles.asList().filter(
            (i: ReturnType<typeof OrganizationRoles.asList>[0]) =>
              i.value !== OrganizationRoles.ADMIN.value,
          );

    return [
      {
        label: t("organizations.labels.firstName"),
        required: true,
        field: (
          <HookFormInput
            name="firstName"
            control={form.control}
            placeholder={t("organizations.placeholders.enterFirstName")}
          />
        ),
      },
      {
        label: t("organizations.labels.lastName"),
        required: true,
        field: (
          <HookFormInput
            name="lastName"
            placeholder={t("organizations.placeholders.enterLastName")}
            control={form.control}
          />
        ),
      },
      {
        label: t("organizations.labels.role"),
        required: true,
        field: (
          <HookFormBaseDropdown
            name="role"
            placeholder={t("organizations.placeholders.selectRole")}
            options={roleOptions}
            control={form.control}
          />
        ),
      },
    ];
  }, [currentUser]);

  const handleSubmit = (values: UpdateMembershipRequest) => {
    patchMembershipById({
      id: defaultData.id,
      data: values,
    })
      .unwrap()
      .then(() => {
        notification.success({
          message: t("organizations.labels.memberUpdated"),
        });
        onClose();
      })
      .catch((errors) => handleErrors(errors, form.setError, notification));
  };

  const onClose = () => {
    form.reset();
    setOpen(false);
  };

  return (
    <BaseModal
      title={t("organizations.labels.updateMembership")}
      open={open}
      onCancel={onClose}
      footer={
        <div className="modal-footer-buttons">
          <BaseButton
            size={BUTTON_SIZES.SMALL}
            variant={BUTTON_VARIANTS.TERTIARY}
            onClick={onClose}
          >
            {t("organizations.labels.cancel")}
          </BaseButton>

          <BaseButton
            variant="primary"
            size="small"
            loading={form.formState.isSubmitting}
            onClick={form.handleSubmit(handleSubmit)}
          >
            {t("organizations.labels.submit")}
          </BaseButton>
        </div>
      }
    >
      <div className="form-input-container">
        {fields.map((field) => (
          <FormFieldWrapper
            key={field.label}
            label={field.label}
            required={field.required}
          >
            {field.field}
          </FormFieldWrapper>
        ))}
      </div>
    </BaseModal>
  );
};

export default MembershipFormModal;
